import { Injectable, type Provider } from '@angular/core';

import { err, makeError, ok, type ApiResult } from '../../../core/models/api-result';
import { PREVIEW_PERSON_NAMES } from '../create/hub-create-preview.service';
import { PREVIEW_JOINED_HUBS } from './hub-page-preview.service';
import type {
  AcceptedHub,
  HubInvite,
  HubInviteSummary,
  HubJoinLink,
  HubJoinLinkState,
  HubJoinPreview,
  HubJoinPreviewStatus,
  HubPersonBrief,
  InviteToHubResult,
} from './hub-invites.models';
import { HubInvitesService, type TeamHubRow } from './hub-invites.service';

/**
 * HUB INVITES, WITHOUT A SESSION — the harness behind `/preview/hubs`.
 *
 * Same bargain as `HubFeedPreviewService`: it overrides only the network
 * seams, so the dedupe, the caps, the "exactly one key" rule and the
 * session's link cache are the real service's. What it answers is held in
 * memory for the page's life.
 *
 * THE CAST. Coffee Club (which the harness viewer owns) has six invites
 * already, so the owner's list shows every column. One invite is waiting for
 * the viewer: `?hub=padel&invite=inv-padel`, from Dean, to Padel Club, which
 * they are not in. `/preview/hubs/join/preview-link` opens the same hub by
 * link, and `?invite=inv-expired` is one whose Join is refused as expired.
 * (The page parks and strips both keys on arrival: hub-join-park.ts.)
 */

const VIEWER: HubPersonBrief = { _id: 'preview-viewer', name: 'You', picture: null };
const DEAN: HubPersonBrief = { _id: 'p1', name: 'Dean Okafor', picture: null };

function person(id: string): HubPersonBrief {
  return { _id: id, name: PREVIEW_PERSON_NAMES[id] ?? 'A colleague', picture: null };
}

const HOURS = 3_600_000;

function seedInvite(
  n: number,
  inviteeId: string,
  status: HubInvite['status'],
  hoursAgo: number,
  canRemind: boolean,
): HubInvite {
  const createdAt = new Date(Date.now() - hoursAgo * HOURS).toISOString();
  return {
    id: `inv-coffee-${n}`,
    hubId: 'coffee',
    hubName: 'Coffee Club',
    inviter: VIEWER,
    invitee: person(inviteeId),
    status,
    message: null,
    createdAt,
    respondedAt: status === 'SENT' || status === 'EXPIRED' ? null : createdAt,
    remindedAt: null,
    canRemind,
  };
}

/** The teams a harness search can find: division hubs, with derived counts. */
const PREVIEW_TEAMS: readonly TeamHubRow[] = [
  { _id: 'div-rdx', name: 'RDx Product', divisionId: 'd-rdx', memberCount: 24 },
  { _id: 'div-design', name: 'Product Design', divisionId: 'd-design', memberCount: 9 },
  { _id: 'div-facilities', name: 'Facilities', divisionId: 'd-facilities', memberCount: 14 },
  { _id: 'div-people', name: 'People and Culture', divisionId: 'd-people', memberCount: 11 },
];

/** Who is already in each harness hub, so a send can report "already in". */
const PREVIEW_MEMBERS: Record<string, readonly string[]> = {
  coffee: ['preview-viewer', 'p4'],
};

const PREVIEW_HUBS: Record<string, { name: string; purpose: string; cover: string | null; members: number }> = {
  padel: {
    name: 'Padel Club',
    purpose: 'Doubles after work on Tuesdays and Thursdays. All levels; rackets to borrow.',
    cover: null,
    members: 63,
  },
  coffee: {
    name: 'Coffee Club',
    purpose: 'Beans, grinders and the machine on Level 2. Who is roasting this week.',
    cover: null,
    members: 35,
  },
};

@Injectable()
export class HubInvitesPreviewService extends HubInvitesService {
  private readonly invites = new Map<string, HubInvite[]>([
    [
      'coffee',
      [
        seedInvite(1, 'p2', 'JOINED', 30, false),
        seedInvite(2, 'p3', 'JOINED', 50, false),
        seedInvite(3, 'p5', 'SENT', 60, true),
        seedInvite(4, 'p6', 'SENT', 4, false),
        seedInvite(5, 'p7', 'EXPIRED', 24 * 31, false),
        seedInvite(6, 'p8', 'DECLINED', 20, false),
      ],
    ],
  ]);

  /** Tokens this harness has handed out, by hub. */
  private readonly tokens = new Map<string, string>([
    ['preview-link', 'padel'],
    // The Lab already has a link out, made by somebody else: its sheet says so
    // rather than offering to make one (hubJoinLink).
    ['lab-link-0', 'lab'],
  ]);
  /** Links revoked or replaced: they still name their hub, with status REVOKED. */
  private readonly retired = new Map<string, string>();
  private readonly linkMadeByMe = new Set<string>();
  private linkSeq = 0;

  private readonly viewerInvites: HubInvite[] = [
    {
      id: 'inv-padel',
      hubId: 'padel',
      hubName: 'Padel Club',
      inviter: DEAN,
      invitee: VIEWER,
      status: 'SENT',
      message: 'Tuesday doubles need a fourth. Come and play.',
      createdAt: new Date(Date.now() - 2 * HOURS).toISOString(),
      respondedAt: null,
      remindedAt: null,
      canRemind: false,
    },
    // Thirty-one days old: the preview still opens (the API's status field is
    // not here yet), and Join is refused as expired, which is the path the
    // card has to handle until it is.
    {
      id: 'inv-expired',
      hubId: 'padel',
      hubName: 'Padel Club',
      inviter: DEAN,
      invitee: VIEWER,
      status: 'SENT',
      message: null,
      createdAt: new Date(Date.now() - 31 * 24 * HOURS).toISOString(),
      respondedAt: null,
      remindedAt: null,
      canRemind: false,
    },
  ];

  private async pause(ms = 150): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  protected override async sendInvite(input: Record<string, unknown>): Promise<ApiResult<InviteToHubResult | null>> {
    await this.pause(250);
    const hubId = String(input['hubId']);
    const list = this.invites.get(hubId) ?? [];
    const members = new Set(PREVIEW_MEMBERS[hubId] ?? []);
    const pending = new Set(list.filter(i => i.status === 'SENT').map(i => i.invitee._id));
    const userIds = (input['userIds'] as string[] | undefined) ?? [];
    const divisionIds = (input['divisionIds'] as string[] | undefined) ?? [];

    let alreadyMembers = 0;
    let alreadyInvited = 0;
    const created: HubInvite[] = [];
    for (const id of userIds) {
      if (members.has(id)) alreadyMembers++;
      else if (pending.has(id)) alreadyInvited++;
      else {
        const invite: HubInvite = {
          ...seedInvite(list.length + created.length + 1, id, 'SENT', 0, false),
          hubId,
          hubName: PREVIEW_HUBS[hubId]?.name ?? 'this hub',
          message: (input['message'] as string | undefined) ?? null,
        };
        created.push(invite);
      }
    }
    // A team expands server-side; the harness counts its members as invited,
    // less a few who are already in, so the summary shows both numbers.
    let teamInvited = 0;
    for (const id of divisionIds) {
      const team = PREVIEW_TEAMS.find(t => t.divisionId === id);
      const size = team?.memberCount ?? 0;
      const inAlready = Math.min(2, size);
      alreadyMembers += inAlready;
      teamInvited += size - inAlready;
    }
    this.invites.set(hubId, [...created, ...list]);
    return ok({
      invited: created.length + teamInvited,
      alreadyMembers,
      alreadyInvited,
      skipped: 0,
      invites: created,
    });
  }

  protected override async fetchSummary(hubId: string): Promise<ApiResult<HubInviteSummary | null>> {
    await this.pause();
    const items = this.invites.get(hubId) ?? [];
    return ok({
      sent: items.length,
      joined: items.filter(i => i.status === 'JOINED').length,
      declined: items.filter(i => i.status === 'DECLINED').length,
      pending: items.filter(i => i.status === 'SENT' || i.status === 'EXPIRED').length,
      items: { totalCount: items.length, hasNextPage: false, endCursor: null, items },
    });
  }

  protected override async fetchMine(): Promise<ApiResult<readonly HubInvite[] | null>> {
    return ok(this.viewerInvites.filter(i => i.status === 'SENT'));
  }

  protected override async fetchPreview(vars: { inviteId?: string; token?: string }): Promise<ApiResult<HubJoinPreview | null>> {
    await this.pause();
    const invite = vars.inviteId ? this.viewerInvites.find(i => i.id === vars.inviteId) : null;
    const liveHub = vars.token ? this.tokens.get(vars.token) : undefined;
    const retiredHub = vars.token ? this.retired.get(vars.token) : undefined;
    const hubId = invite?.hubId ?? liveHub ?? retiredHub;
    const hub = hubId ? PREVIEW_HUBS[hubId] : undefined;
    if (!hubId || !hub) {
      return err(makeError('NOT_FOUND', 'This link is not valid. Ask for a new one.', { retryable: false }));
    }
    const alreadyMember =
      (PREVIEW_MEMBERS[hubId] ?? []).includes('preview-viewer') || PREVIEW_JOINED_HUBS.has(hubId);
    // As the API now answers: a status, never an error, for a dead invite.
    const status: HubJoinPreviewStatus = alreadyMember
      ? 'ALREADY_MEMBER'
      : invite?.id === 'inv-expired'
        ? 'EXPIRED'
        : invite?.status === 'DECLINED'
          ? 'DECLINED'
          : invite?.status === 'JOINED'
            ? 'ALREADY_MEMBER'
            : retiredHub && !liveHub
              ? 'REVOKED'
              : 'OPEN';
    const faces = ['p2', 'p3', 'p4', 'p5', 'p9'].map(person);
    return ok({
      hubId,
      name: hub.name,
      purpose: hub.purpose,
      coverUrl: hub.cover,
      memberCount: hub.members,
      facesSample: faces,
      alreadyMember,
      inviterName: invite ? invite.inviter.name : null,
      status,
    });
  }

  protected override async sendCreateLink(hubId: string): Promise<ApiResult<HubJoinLink | null>> {
    await this.pause();
    this.retire(hubId);
    const token = `${hubId}-link-${++this.linkSeq}`;
    this.tokens.set(token, hubId);
    this.linkMadeByMe.add(hubId);
    return ok({
      url: `${location.origin}/#/hubs/join/${token}`,
      token,
      expiresAt: new Date(Date.now() + 30 * 24 * HOURS).toISOString(),
    });
  }

  protected override async sendRevokeLink(hubId: string): Promise<ApiResult<boolean | null>> {
    await this.pause();
    this.retire(hubId);
    this.linkMadeByMe.delete(hubId);
    return ok(true);
  }

  protected override async fetchLinkState(hubId: string): Promise<ApiResult<HubJoinLinkState | null>> {
    const exists = [...this.tokens.values()].includes(hubId);
    return ok({
      exists,
      expiresAt: exists ? new Date(Date.now() + 19 * 24 * HOURS).toISOString() : null,
      createdByMe: this.linkMadeByMe.has(hubId),
    });
  }

  private retire(hubId: string): void {
    for (const [token, hub] of this.tokens) {
      if (hub !== hubId) continue;
      this.tokens.delete(token);
      this.retired.set(token, hub);
    }
  }

  protected override async sendAccept(vars: { inviteId?: string; token?: string }): Promise<ApiResult<AcceptedHub | null>> {
    await this.pause(200);
    const invite = vars.inviteId ? this.viewerInvites.find(i => i.id === vars.inviteId) : null;
    const hubId = invite?.hubId ?? (vars.token ? this.tokens.get(vars.token) : undefined);
    if (!hubId) return err(makeError('NOT_FOUND', 'This invite is no longer open.', { retryable: false }));
    if (invite?.id === 'inv-expired') {
      return err(makeError('VALIDATION', 'This invite has expired. Ask for a new one.', { retryable: false }));
    }
    if (invite) this.answer(invite.id, 'JOINED');
    PREVIEW_JOINED_HUBS.add(hubId);
    return ok({ _id: hubId, name: PREVIEW_HUBS[hubId]?.name ?? 'the hub' });
  }

  protected override async sendDecline(inviteId: string): Promise<ApiResult<boolean | null>> {
    await this.pause();
    this.answer(inviteId, 'DECLINED');
    return ok(true);
  }

  protected override async sendRemind(inviteId: string): Promise<ApiResult<HubInvite | null>> {
    await this.pause();
    for (const [hubId, list] of this.invites) {
      const at = list.findIndex(i => i.id === inviteId);
      if (at < 0) continue;
      const reminded: HubInvite = { ...list[at], remindedAt: new Date().toISOString(), canRemind: false };
      const next = [...list];
      next[at] = reminded;
      this.invites.set(hubId, next);
      return ok(reminded);
    }
    return err(makeError('NOT_FOUND', 'That invite has gone.', { retryable: false }));
  }

  protected override async fetchTeams(search: string, first: number): Promise<ApiResult<{ items: readonly TeamHubRow[] } | null>> {
    const q = search.toLowerCase();
    return ok({ items: PREVIEW_TEAMS.filter(t => t.name.toLowerCase().includes(q)).slice(0, first) });
  }

  private answer(inviteId: string, status: HubInvite['status']): void {
    const at = this.viewerInvites.findIndex(i => i.id === inviteId);
    if (at >= 0) this.viewerInvites[at] = { ...this.viewerInvites[at], status, respondedAt: new Date().toISOString() };
  }
}

/** Drop into a route's `providers` to answer invites from memory. */
export const HUB_INVITES_PREVIEW_PROVIDER: Provider = {
  provide: HubInvitesService,
  useClass: HubInvitesPreviewService,
};
